import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { htmlToText } from 'html-to-text';

export type DocumentFormat = 'pdf' | 'docx' | 'txt' | 'html';

export interface ConversionOptions {
  documentContent: string;
  documentTitle: string;
  documentType: string;
  format: DocumentFormat;
}

class DocumentConverterService {
  async convertToPDF(content: string, title: string): Promise<Blob> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const plainText = htmlToText(content, {
      wordwrap: 130,
      preserveNewlines: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    const lineHeight = 7;
    let yPosition = margin;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, yPosition);
    yPosition += lineHeight * 2;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const lines = plainText.split('\n');

    for (const line of lines) {
      if (yPosition + lineHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }

      const wrappedLines = doc.splitTextToSize(line || ' ', maxWidth);

      for (const wrappedLine of wrappedLines) {
        if (yPosition + lineHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        doc.text(wrappedLine, margin, yPosition);
        yPosition += lineHeight;
      }
    }

    return doc.output('blob');
  }

  async convertToDOCX(content: string, title: string): Promise<Blob> {
    const plainText = htmlToText(content, {
      wordwrap: false,
      preserveNewlines: true,
    });

    const paragraphs: Paragraph[] = [
      new Paragraph({
        text: title,
        heading: HeadingLevel.HEADING_1,
        spacing: {
          after: 400,
        },
        alignment: AlignmentType.CENTER,
      }),
    ];

    const lines = plainText.split('\n');

    for (const line of lines) {
      if (line.trim()) {
        const isHeading = line.match(/^#+\s/) || line.match(/^[A-Z\s]{5,}$/);

        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: line,
                bold: isHeading ? true : false,
                size: isHeading ? 28 : 22,
              }),
            ],
            spacing: {
              after: 200,
            },
          })
        );
      } else {
        paragraphs.push(
          new Paragraph({
            text: '',
            spacing: { after: 100 },
          })
        );
      }
    }

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: paragraphs,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    return blob;
  }

  convertToTXT(content: string, title: string): Blob {
    const plainText = htmlToText(content, {
      wordwrap: 80,
      preserveNewlines: true,
    });

    const fullContent = `${title}\n${'='.repeat(title.length)}\n\n${plainText}`;

    return new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
  }

  convertToHTML(content: string, title: string): Blob {
    const styledHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    body {
      font-family: 'Georgia', serif;
      line-height: 1.6;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      background-color: #f9fafb;
      color: #1f2937;
    }
    h1 {
      color: #111827;
      border-bottom: 3px solid #3b82f6;
      padding-bottom: 10px;
      margin-bottom: 30px;
    }
    h2 {
      color: #374151;
      margin-top: 30px;
      margin-bottom: 15px;
    }
    h3 {
      color: #4b5563;
      margin-top: 20px;
      margin-bottom: 10px;
    }
    p {
      margin-bottom: 15px;
      text-align: justify;
    }
    .content {
      background-color: white;
      padding: 40px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      border-radius: 8px;
    }
    @media print {
      body {
        background-color: white;
      }
      .content {
        box-shadow: none;
        padding: 0;
      }
    }
  </style>
</head>
<body>
  <div class="content">
    <h1>${title}</h1>
    ${content}
  </div>
</body>
</html>`;

    return new Blob([styledHTML], { type: 'text/html;charset=utf-8' });
  }

  async convert(options: ConversionOptions): Promise<Blob> {
    const { documentContent, documentTitle, format } = options;

    switch (format) {
      case 'pdf':
        return this.convertToPDF(documentContent, documentTitle);
      case 'docx':
        return this.convertToDOCX(documentContent, documentTitle);
      case 'txt':
        return this.convertToTXT(documentContent, documentTitle);
      case 'html':
        return this.convertToHTML(documentContent, documentTitle);
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  }

  async downloadDocument(options: ConversionOptions): Promise<void> {
    try {
      const blob = await this.convert(options);
      const fileName = `${options.documentType}_${new Date().toISOString().split('T')[0]}.${options.format}`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw new Error(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  getFormatLabel(format: DocumentFormat): string {
    const labels: Record<DocumentFormat, string> = {
      pdf: 'PDF Document',
      docx: 'Word Document',
      txt: 'Plain Text',
      html: 'HTML File',
    };
    return labels[format];
  }

  getFormatIcon(format: DocumentFormat): string {
    const icons: Record<DocumentFormat, string> = {
      pdf: '📄',
      docx: '📝',
      txt: '📋',
      html: '🌐',
    };
    return icons[format];
  }
}

export const documentConverter = new DocumentConverterService();
